import type { TimelineRunsReportResponse } from "../types";
import { RunsTruncatedNotice } from "./RunsTruncatedNotice";
import { TimelineControls } from "./Timeline/Controls";
import { TimelineCursor, TimelineTooltip } from "./Timeline/CursorLayer";
import { GridLayer } from "./Timeline/GridLayer";
import { TimelineTableBody } from "./Timeline/TableBody";
import { useTimelineState } from "./Timeline/useTimelineState";

export function Timeline({
  data,
  loading,
  error,
}: {
  data: TimelineRunsReportResponse | null;
  loading: boolean;
  error: string | null;
}) {
  const {
    tableContainerRef,
    tableRef,
    hover,
    cursor,
    layout,
    setRangeDays,
    splitByFunction,
    setSplitByFunction,
    onDotEnter,
    onDotLeave,
    onBarClick,
    onTableMove,
    onTableLeave,
    dayGridLines,
  } = useTimelineState(data);

  const { projectGroups, minMs, maxMs, hasData, availableDays, clampedRangeDays } = layout;

  if (loading) {
    return (
      <div className="page-view empty-state max-w-[1600px] font-mono">
        <div className="dv-spinner" />
        <p>Loading timeline...</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="page-view empty-state max-w-[1600px] font-mono">
        <p>⚠️ {error}</p>
      </div>
    );
  }

  return (
    <div className="page-view max-w-[1600px] font-mono">
      <div className="section-header">
        <h2 className="text-2xl">Timeline</h2>
        <TimelineControls
          hasData={hasData}
          minMs={minMs}
          maxMs={maxMs}
          clampedRangeDays={clampedRangeDays}
          availableDays={availableDays}
          splitByFunction={splitByFunction}
          setSplitByFunction={setSplitByFunction}
          setRangeDays={setRangeDays}
        />
      </div>
      <RunsTruncatedNotice data={data} />
      <div
        className="admin-table-container relative"
        ref={tableContainerRef}
        onMouseMove={onTableMove}
        onMouseLeave={onTableLeave}
      >
        <table className="admin-table timeline-table" ref={tableRef}>
          <thead>
            <tr>
              <th className="cell-project">Project</th>
              <th className="cell-connector">Connector</th>
              {splitByFunction ? <th className="cell-function">Function</th> : null}
              <th className="th-timeline w-full">
                <span className="sr-only">Timeline</span>
              </th>
            </tr>
          </thead>
          <TimelineTableBody
            hasData={hasData}
            splitByFunction={splitByFunction}
            projectGroups={projectGroups}
            hover={hover}
            onDotEnter={onDotEnter}
            onDotLeave={onDotLeave}
            onBarClick={onBarClick}
          />
        </table>
        {hasData && <GridLayer lines={dayGridLines} />}
        <TimelineCursor cursor={cursor} />
      </div>
      <TimelineTooltip hover={hover} />
    </div>
  );
}
